/* eslint-disable max-len */
import React, { useMemo, useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, ArrowRight, Stethoscope } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { SYMPTOM_PAGES, SPECIALTIES } from '../data/symptoms';
import { useSeo } from '../hooks/useSeo';

const ALL = 'All';

// Map specialty -> compact label used by the chip
const CHIP_LABEL = {
  'General Medicine': 'General',
};

export const SymptomsIndex = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const [activeSpecialty, setActiveSpecialty] = useState(() => {
    const fromUrl = searchParams.get('specialty');
    return fromUrl && SPECIALTIES.includes(fromUrl) ? fromUrl : ALL;
  });

  // Keep URL ?specialty=... in sync with active chip (without polluting browser history).
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (activeSpecialty === ALL) next.delete('specialty');
    else next.set('specialty', activeSpecialty);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSpecialty]);

  useSeo({
    title: 'Symptom Guides | Healthwrx Multispeciality Clinic Guwahati',
    description:
      'Browse 23 expert-reviewed symptom guides from Healthwrx Guwahati — covering urology, orthopedics, dermatology and more. Find the right specialist for your concern.',
  });

  // Pre-compute counts per specialty (for chip badges)
  const countsBySpecialty = useMemo(() => {
    const map = { [ALL]: SYMPTOM_PAGES.length };
    SPECIALTIES.forEach((s) => {
      map[s] = SYMPTOM_PAGES.filter((p) => p.department === s).length;
    });
    return map;
  }, []);

  // Specialties that actually have at least one page (avoid empty chips if any)
  const visibleSpecialties = useMemo(
    () => SPECIALTIES.filter((s) => countsBySpecialty[s] > 0),
    [countsBySpecialty]
  );

  // Filter + group
  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const pages = SYMPTOM_PAGES.filter((p) => {
      const specOk = activeSpecialty === ALL || p.department === activeSpecialty;
      if (!specOk) return false;
      if (!q) return true;
      const hay = [
        p.aboutCondition,
        p.h1,
        p.subheadline,
        p.focusKeyword,
        p.department,
        ...(p.glance?.causes || []),
        ...(p.careOptions || []),
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
    // Group by specialty preserving SPECIALTIES order
    return visibleSpecialties
      .map((s) => ({ specialty: s, items: pages.filter((p) => p.department === s) }))
      .filter((g) => g.items.length > 0);
  }, [query, activeSpecialty, visibleSpecialties]);

  const totalShown = groups.reduce((n, g) => n + g.items.length, 0);

  return (
    <div className="min-h-screen pt-20 bg-gray-50" data-testid="symptoms-index-page">
      {/* HERO */}
      <section className="py-12 md:py-16 bg-gradient-to-br from-[#1C5C62] to-[#14D1A2] text-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <p className="text-sm uppercase tracking-widest text-[#9DF5D8] mb-3">Healthwrx · Patient Guides</p>
          <h1 className="text-4xl md:text-5xl font-bold mb-3 max-w-3xl">Symptom Guides</h1>
          <p className="text-base md:text-lg text-gray-100 max-w-3xl">
            Honest, easy-to-understand information about {SYMPTOM_PAGES.length} common health concerns —
            with guidance on when to see a specialist at Healthwrx Guwahati.
          </p>
        </div>
      </section>

      {/* STICKY FILTER BAR */}
      <div className="sticky top-16 z-30 bg-white/90 backdrop-blur border-b border-gray-200">
        <div className="container mx-auto px-4 max-w-6xl py-4 space-y-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search symptoms (e.g. back pain, fever, hair fall)"
              className="pl-10 h-11 text-sm"
              data-testid="symptoms-search-input"
            />
          </div>
          {/* Chips */}
          <div className="flex flex-wrap gap-2" data-testid="symptoms-specialty-chips">
            <Chip
              label={ALL}
              count={countsBySpecialty[ALL]}
              active={activeSpecialty === ALL}
              onClick={() => setActiveSpecialty(ALL)}
              testId="chip-all"
            />
            {visibleSpecialties.map((s) => (
              <Chip
                key={s}
                label={CHIP_LABEL[s] || s}
                count={countsBySpecialty[s]}
                active={activeSpecialty === s}
                onClick={() => setActiveSpecialty(s)}
                testId={`chip-${s.toLowerCase().replace(/\s+/g, '-')}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* RESULTS */}
      <div className="container mx-auto px-4 max-w-6xl py-10">
        {/* Result count */}
        <p className="text-sm text-gray-500 mb-6" data-testid="symptoms-result-count">
          Showing <span className="font-semibold text-gray-900">{totalShown}</span> of {SYMPTOM_PAGES.length} guides
          {query && (
            <>
              {' '}for "<span className="text-[#1C5C62] font-medium">{query}</span>"
            </>
          )}
          {activeSpecialty !== ALL && (
            <>
              {' '}in <span className="text-[#1C5C62] font-medium">{activeSpecialty}</span>
            </>
          )}
        </p>

        {/* Empty state */}
        {totalShown === 0 && (
          <Card className="border-dashed border-2 border-gray-200 bg-transparent" data-testid="symptoms-empty-state">
            <CardContent className="p-10 text-center">
              <Stethoscope className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600 font-medium">No guides match your search.</p>
              <p className="text-sm text-gray-500 mt-1">
                Try a different keyword, or{' '}
                <button
                  type="button"
                  onClick={() => {
                    setQuery('');
                    setActiveSpecialty(ALL);
                  }}
                  className="text-[#14D1A2] underline-offset-4 hover:underline font-medium"
                  data-testid="symptoms-clear-filters"
                >
                  clear filters
                </button>
                .
              </p>
            </CardContent>
          </Card>
        )}

        {/* Groups */}
        <div className="space-y-12">
          {groups.map((g) => (
            <section key={g.specialty} data-testid={`symptoms-group-${g.specialty.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className="flex items-baseline justify-between mb-5">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900">
                  {g.specialty}
                </h2>
                <span className="text-xs uppercase tracking-wider text-gray-500">
                  {g.items.length} {g.items.length === 1 ? 'guide' : 'guides'}
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {g.items.map((p) => (
                  <SymptomCard key={p.slug} page={p} />
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
};

const Chip = ({ label, count, active, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-sm font-medium border transition ${
      active
        ? 'bg-[#1C5C62] text-white border-[#1C5C62] shadow-sm'
        : 'bg-white text-gray-700 border-gray-200 hover:border-[#14D1A2] hover:text-[#1C5C62]'
    }`}
  >
    {label}
    <span
      className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
        active ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600'
      }`}
    >
      {count}
    </span>
  </button>
);

const SymptomCard = ({ page }) => (
  <Link
    to={`/${page.slug}`}
    className="group block bg-white border border-gray-100 rounded-xl p-6 shadow-sm hover:shadow-lg hover:border-[#14D1A2]/40 transition"
    data-testid={`symptom-card-${page.slug}`}
  >
    <p className="text-[11px] uppercase tracking-wider text-[#14D1A2] font-semibold mb-2">
      {page.department}
    </p>
    <h3 className="text-base md:text-lg font-bold text-gray-900 leading-snug mb-2">
      {page.aboutCondition}
    </h3>
    <p className="text-sm text-gray-600 leading-relaxed line-clamp-3 mb-4">
      {page.subheadline}
    </p>
    <span className="inline-flex items-center gap-1 text-sm font-medium text-[#14D1A2] group-hover:gap-2 transition-all">
      Read guide <ArrowRight className="w-4 h-4" />
    </span>
  </Link>
);

export default SymptomsIndex;
